import { clientLogger } from '@/lib/clientLogger';
import { App } from '@capacitor/app';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { isAndroid, isNative } from './capacitor';

export const BUILTIN_APP_VERSION = __APP_VERSION__;
export const ANDROID_VERSION_CODE = __ANDROID_VERSION_CODE__;

export type AppVersionInfo = {
  bundleVersion: string;
  builtinVersion: string;
  nativeVersionCode: number;
  platform: 'web' | 'android' | 'ios';
};

function resolveBundleVersion(version?: string | null): string {
  if (!version || version === 'builtin') {
    return BUILTIN_APP_VERSION;
  }

  return version;
}

function parseSemver(version: string): number[] {
  return version
    .trim()
    .replace(/^v/i, '')
    .split('-')[0]
    .split('.')
    .map((segment) => Number.parseInt(segment, 10) || 0);
}

export async function getAppVersion(): Promise<AppVersionInfo> {
  const platform = isAndroid() ? 'android' : isNative() ? 'ios' : 'web';

  if (platform === 'android') {
    try {
      const [current, info] = await Promise.all([
        CapacitorUpdater.current(),
        App.getInfo(),
      ]);

      // The builtin bundle reports its `version` as the native versionName ("1.0"),
      // so identify it by `id` and substitute the real package.json version instead.
      const isBuiltin = current.bundle.id === 'builtin';
      const nativeVersionCode = Number.parseInt(info.build, 10);

      return {
        bundleVersion: isBuiltin
          ? BUILTIN_APP_VERSION
          : resolveBundleVersion(current.bundle.version),
        builtinVersion: BUILTIN_APP_VERSION,
        nativeVersionCode: Number.isNaN(nativeVersionCode)
          ? ANDROID_VERSION_CODE
          : nativeVersionCode,
        platform,
      };
    } catch (error) {
      clientLogger.warn('[OTA] Failed to read current bundle version:', error);
    }
  }

  return {
    bundleVersion: BUILTIN_APP_VERSION,
    builtinVersion: BUILTIN_APP_VERSION,
    nativeVersionCode: ANDROID_VERSION_CODE,
    platform,
  };
}

export function compareSemver(a: string, b: string): -1 | 0 | 1 {
  const left = parseSemver(a);
  const right = parseSemver(b);
  const maxLength = Math.max(left.length, right.length, 3);

  for (let index = 0; index < maxLength; index += 1) {
    const leftPart = left[index] ?? 0;
    const rightPart = right[index] ?? 0;

    if (leftPart < rightPart) {
      return -1;
    }

    if (leftPart > rightPart) {
      return 1;
    }
  }

  return 0;
}